import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger } from "@fluentui/react-components";
import { Delete16Regular } from "@fluentui/react-icons";
import { RawMaterial } from "../redux/rawMaterialControl";
import { useRawMaterialControl } from "../redux/ReduxContext";
import PowderPieChart from "../PowderOrder/PowderPieChart";
import { GetComposition, PowderComposition, PowderInventoryGroupedByDescription } from "../../../services/PowderFormService";
import CustomDropdown from "../CustomDropdown";
import { DecimalInput } from "../../DecimalInput";
import { parseDecimal } from "../../../utils/decimal";

interface RawMaterialOrderRowProps {
    index: number;
    inventory: PowderInventoryGroupedByDescription[];
    onChangeLot: (index: number, nextLot: string) => void;
};

export const LotCell = ({ row, cellBackground, hasComposition }: { row: RawMaterial; cellBackground: () => string; hasComposition: boolean }) => {

    const content = (
        <div
            className={`h-full flex items-center justify-center text-primary-dark dark:text-primary-light text-xs overflow-hidden ${hasComposition ? 'cursor-pointer' : ''} ${cellBackground}`}
        >
            <span
                className={`bloc w-full truncate ml-4 text-left ${hasComposition ? 'underline' : ''}`}
                title={row.lot}
            >
                {row.lot}
            </span>
        </div>
    );

    if (!hasComposition) {
        return content;
    }
    return (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                {content}
            </DialogTrigger>

            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Lot Details</DialogTitle>
                    <DialogContent>
                        <PowderPieChart data={row.powderComposition} />
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
export const RawMaterialOrderRow: React.FC<RawMaterialOrderRowProps> = ({
    index,
    inventory,
    onChangeLot,
}) => {
    const { state, dispatch } = useRawMaterialControl();
    const lotTriggerRef = useRef<HTMLButtonElement>(null);
    const [isLotDropdownOpen, setIsLotDropdownOpen] = useState(false);
    const row = state.materials[index];
    const rowLot = row?.lot ?? "";
    const rowRvid = row?.rvid ?? "";
    const availableLots = inventory.find((item) => item.Description === row?.raw_mtrl)?.Lots ?? [];
    const selectedLotItem = availableLots.find((lotItem) => lotItem.Lot === rowLot);

    useEffect(() => {
        if (!rowLot || !rowRvid) {
            return;
        }

        let oxygenPercent = 0;
        let carbonPercent = 0;
        const powderComposition: PowderComposition[] = [];
        GetComposition(rowRvid, rowLot).then(composition => {
            if (composition) {
                powderComposition.push(...composition);
                composition.forEach(element => {
                    if (element.Symbol === "O") {
                        oxygenPercent = element.Percentage;
                    }
                    if (element.Symbol === "C") {
                        carbonPercent = element.Percentage;
                    }
                });

                dispatch({
                    type: 'UPDATE_ARRAY_ITEM', payload: {
                        field: 'materials', index: index, value: (prevItem: RawMaterial) => ({
                            ...prevItem,
                            powderComposition,
                            oPercent: oxygenPercent,
                            cPercent: carbonPercent,
                        })
                    }
                });
            }
        });

    }, [dispatch, index, rowLot, rowRvid])

    if (!row) {
        return null;
    }

    const cellBackground = () => {
        if (row.raw_mtrl?.includes("PRZ")) return "bg-primary-dark/20 dark:bg-primary-dark/50";
        if (row.raw_mtrl?.includes("PEG")) return "bg-primary-light dark:bg-primary-dark";

        return "bg-primary-light dark:bg-primary-dark";
    }
    const inputClass = "focus:outline-none bg-primary-light dark:bg-primary-dark h-[32px] w-[92px] min-w-[92px] max-w-[92px] text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold border px-2 text-center dark:border-primary-light/50 border-primary-dark/50";
    return (
        <tr className="h-[48px] flex">
            <td className="w-8 flex-none">
                <div className="h-full w-7 items-center justify-center flex">
                    <button
                        type="button"
                        className="w-4 h-4 sm:w-6 sm:h-6 flex items-center justify-center text-primary-dark dark:text-primary-light hover:opacity-80"
                        aria-label="Delete row"
                        onClick={() => {
                            dispatch({ type: 'DELETE_ARRAY_ITEM', payload: { field: 'materials', index: index } })
                        }}
                    >
                        <Delete16Regular className="w-4 h-4" />
                    </button>
                </div>
            </td>
            <td className={` w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] flex-none `}>
                <div className={`h-full flex items-center px-2 text-primary-dark dark:text-primary-light text-xs truncate ${cellBackground()}`} title={row.raw_mtrl}>{row.raw_mtrl}</div>
            </td>
            <td className="flex-none w-[96px] min-w-[96px] max-w-[96px] sm:w-[168px] sm:min-w-[168px] sm:max-w-[168px] overflow-hidden">
                <div className={`h-full flex items-stretch ${cellBackground()}`}>
                    <div className="min-w-0 flex-1 overflow-hidden border-primary-dark/20 dark:border-primary-light/20">
                        <LotCell row={row} cellBackground={cellBackground} hasComposition={selectedLotItem?.HasComposition ?? false} />
                    </div>
                    <button
                        ref={lotTriggerRef}
                        type="button"
                        className="relative z-[10000] flex w-[36px] min-w-[36px] items-center justify-center"
                        aria-label={`Select lot for ${row.raw_mtrl}`}
                        onClick={() => setIsLotDropdownOpen(!isLotDropdownOpen)}
                    >
                        <svg
                            className="w-4 h-4 text-primary-dark dark:text-primary-light"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    <CustomDropdown
                        value={rowLot}
                        options={
                            inventory
                                .find((item) => item.Description === row?.raw_mtrl)
                                ?.Lots?.map((lotItem) => ({
                                    label: lotItem.Lot,
                                    value: lotItem.Lot,
                                    HasComposition: lotItem.HasComposition,
                                })) ?? []
                        }
                        onChange={(val) => onChangeLot(index, val)}
                        open={isLotDropdownOpen}
                        onOpenChange={setIsLotDropdownOpen}
                        targetRef={lotTriggerRef}
                        surfaceMinWidth={168}
                    />
                </div>
            </td>
            <td className="w-[120px] min-w-[120px] max-w-[120px] flex-none">
                <div className={`h-full flex items-center justify-center p-1 ${cellBackground()}`}>
                    <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">
                        {row.inStockGrams ?? 0} ggg
                    </span>
                </div>
            </td>

            <td className="w-[120px] min-w-[120px] max-w-[120px] flex-none">
                <div className={`h-full flex items-center justify-center p-1 ${cellBackground()}`}>
                    <DecimalInput
                        pattern="^[0-9]*[,.]?[0-9]*$"
                        className={inputClass}
                        value={row.weightG}
                        onValueChange={(value) => {
                            const parsedValue = parseDecimal(value) ?? undefined;
                            dispatch({
                                type: 'UPDATE_ARRAY_ITEM', payload: {
                                    field: 'materials', index: index || 0, value: (prevItem: RawMaterial) => ({
                                        ...prevItem,
                                        weightG: parsedValue
                                    })
                                }
                            })
                        }}
                        placeholder="0"
                    />
                </div>
            </td>

        </tr>
    );
};
